// frontend/components/HrManagerVacationsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchManagerLeaveRequests,
  approveLeaveRequest,
  rejectLeaveRequest,
  fetchEmployeesList,
  createManagerLeaveRequest,
  updateManagerLeaveRequest,
  deleteManagerLeaveRequest,
} from "../features/hr/hrThunks.js";
import { CalendarDays, Users, Check, X, Pencil, Trash2 } from "lucide-react";
import "../styles/hr/hr-manager-vacations.css";

const renderStatusLabel = (status) => {
  if (status === "pending") return "Pendiente";
  if (status === "approved") return "Aprobada";
  if (status === "rejected") return "Rechazada";
  if (status === "cancelled") return "Cancelada";
  if (status === "all") return "Todos";
  return status || "-";
};

const renderLeaveTypeLabel = (type) => {
  if (type === "vacation") return "Vacaciones";
  if (type === "day_off") return "Día libre";
  if (type === "sick_leave") return "Baja médica";
  if (type === "other") return "Otros";
  return type || "-";
};

const getStatusBadgeClass = (status) => {
  if (status === "pending") return "hr-badge hr-badge--pending";
  if (status === "approved") return "hr-badge hr-badge--success";
  if (status === "rejected") return "hr-badge hr-badge--danger";
  if (status === "cancelled") return "hr-badge hr-badge--neutral";
  return "hr-badge";
};

const getEmployeeName = (leaveRequest) => {
  const employee = leaveRequest.employee || {};
  const user = employee.user || {};
  const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();
  return fullName || user.username || user.email || "Sin nombre";
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

const HrManagerVacationsTab = () => {
  const dispatch = useDispatch();

  const [statusFilter, setStatusFilter] = useState("all");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  // Crear ausencia (manager)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [leaveType, setLeaveType] = useState("vacation");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [requestToEdit, setRequestToEdit] = useState(null);
  const [editLeaveType, setEditLeaveType] = useState("vacation");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editReason, setEditReason] = useState("");

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  // Solicitudes manager
  const managerLeaveRequestsRaw = useSelector((state) => state.hr.managerLeaveRequests) || [];
  const loadingManagerLeaveRequests = useSelector((state) => state.hr.loadingManagerLeaveRequests);
  const errorManagerLeaveRequests = useSelector((state) => state.hr.errorManagerLeaveRequests);

  // Empleados
  const employeesListRaw = useSelector((state) => state.hr.employeesList) || [];
  const loadingEmployeesList = useSelector((state) => state.hr.loadingEmployeesList);
  const errorEmployeesList = useSelector((state) => state.hr.errorEmployeesList);

  // Creación
  const loadingCreateManagerLeaveRequest = useSelector(
    (state) => state.hr.loadingCreateManagerLeaveRequest
  );
  const errorCreateManagerLeaveRequest = useSelector(
    (state) => state.hr.errorCreateManagerLeaveRequest
  );

  // Update / Delete (manager)
  const loadingUpdateManagerLeaveRequest = useSelector(
    (state) => state.hr.loadingUpdateManagerLeaveRequest
  );
  const errorUpdateManagerLeaveRequest = useSelector(
    (state) => state.hr.errorUpdateManagerLeaveRequest
  );

  const loadingDeleteManagerLeaveRequest = useSelector(
    (state) => state.hr.loadingDeleteManagerLeaveRequest
  );
  const errorDeleteManagerLeaveRequest = useSelector(
    (state) => state.hr.errorDeleteManagerLeaveRequest
  );

  // Normalizar paginado { results: [] }
  const employeesList = useMemo(() => {
    if (Array.isArray(employeesListRaw)) return employeesListRaw;
    if (employeesListRaw && Array.isArray(employeesListRaw.results)) return employeesListRaw.results;
    return [];
  }, [employeesListRaw]);

  const managerLeaveRequests = useMemo(() => {
    if (Array.isArray(managerLeaveRequestsRaw)) return managerLeaveRequestsRaw;
    if (managerLeaveRequestsRaw && Array.isArray(managerLeaveRequestsRaw.results)) {
      return managerLeaveRequestsRaw.results;
    }
    return [];
  }, [managerLeaveRequestsRaw]);

  const hasRequests = managerLeaveRequests.length > 0;

  // Cargar empleados una vez
  useEffect(() => {
    if (!employeesList || employeesList.length === 0) {
      dispatch(fetchEmployeesList());
    }
  }, [dispatch, employeesList]);

  const currentFilters = useMemo(() => {
    const filters = {};
    if (statusFilter && statusFilter !== "all") filters.status = statusFilter;
    return filters;
  }, [statusFilter]);

  // Carga inicial: pendientes
  useEffect(() => {
    if (!hasLoadedOnce) {
      setHasLoadedOnce(true);
      dispatch(fetchManagerLeaveRequests(currentFilters));
    }
  }, [dispatch, hasLoadedOnce, currentFilters]);

  const handleSearch = () => {
    dispatch(fetchManagerLeaveRequests(currentFilters));
  };

  const refreshList = () => {
    dispatch(fetchManagerLeaveRequests(currentFilters));
  };

  const handleApprove = async (leaveRequest) => {
    if (processingId) return;

    setProcessingId(leaveRequest.id);
    try {
      await dispatch(
        approveLeaveRequest({
          leaveRequestId: leaveRequest.id,
          managerComment: "",
        })
      ).unwrap();

      refreshList();
    } catch (error) {
      console.error("Error al aprobar solicitud:", error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (leaveRequest) => {
    if (processingId) return;

    setProcessingId(leaveRequest.id);
    try {
      await dispatch(
        rejectLeaveRequest({
          leaveRequestId: leaveRequest.id,
          managerComment: "",
        })
      ).unwrap();

      refreshList();
    } catch (error) {
      console.error("Error al rechazar solicitud:", error);
    } finally {
      setProcessingId(null);
    }
  };

  // Crear ausencia para un empleado
  const handleCreateForEmployee = async (event) => {
    event.preventDefault();
    setCreateSuccessMessage("");

    if (!selectedEmployeeId || !startDate || !endDate) return;

    try {
      await dispatch(
        createManagerLeaveRequest({
          employeeId: selectedEmployeeId,
          leaveType,
          startDate,
          endDate,
          reason,
        })
      ).unwrap();

      setCreateSuccessMessage("Ausencia creada correctamente.");
      setStartDate("");
      setEndDate("");
      setReason("");

      refreshList();
    } catch (error) {
      console.error("Error al crear ausencia para empleado:", error);
    }
  };

  // ---- Edit modal helpers ----
  const openEditModal = (request) => {
    if (!request?.id) return;
    if (processingId) return;
    if (loadingUpdateManagerLeaveRequest || loadingDeleteManagerLeaveRequest) return;

    setRequestToEdit(request);
    setEditLeaveType(request.leave_type || "vacation");
    setEditStartDate(request.start_date || "");
    setEditEndDate(request.end_date || "");
    setEditReason(request.reason || "");
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (loadingUpdateManagerLeaveRequest) return;
    setEditModalOpen(false);
    setRequestToEdit(null);
    setEditLeaveType("vacation");
    setEditStartDate("");
    setEditEndDate("");
    setEditReason("");
  };

  const confirmEdit = async () => {
    if (!requestToEdit?.id) return;
    if (!editStartDate || !editEndDate) return;
    if (loadingUpdateManagerLeaveRequest) return;

    try {
      await dispatch(
        updateManagerLeaveRequest({
          leaveRequestId: requestToEdit.id,
          payload: {
            leave_type: editLeaveType,
            start_date: editStartDate,
            end_date: editEndDate,
            reason: editReason,
          },
        })
      ).unwrap();

      closeEditModal();
      refreshList();
    } catch (error) {
      console.error("Error al editar solicitud:", error);
    }
  };

  // ---- Delete modal helpers ----
  const openDeleteModal = (request) => {
    if (!request?.id) return;
    if (processingId) return;
    if (loadingDeleteManagerLeaveRequest || loadingUpdateManagerLeaveRequest) return;

    setRequestToDelete(request);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (loadingDeleteManagerLeaveRequest) return;
    setDeleteModalOpen(false);
    setRequestToDelete(null);
  };

  const confirmDelete = async () => {
    if (!requestToDelete?.id) return;
    if (loadingDeleteManagerLeaveRequest) return;

    try {
      await dispatch(deleteManagerLeaveRequest(requestToDelete.id)).unwrap();
      closeDeleteModal();
      refreshList();
    } catch (error) {
      console.error("Error al eliminar solicitud:", error);
    }
  };

  // ESC + scroll lock (edit)
  useEffect(() => {
    if (!editModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      closeEditModal();
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [editModalOpen, loadingUpdateManagerLeaveRequest]);

  // ESC + scroll lock (delete)
  useEffect(() => {
    if (!deleteModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      closeDeleteModal();
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [deleteModalOpen, loadingDeleteManagerLeaveRequest]);

  const metricTotal = managerLeaveRequests.length;

  const employeeOptions = useMemo(() => {
    return safeArray(employeesList)
      .map((employee) => {
        const user = employee.user || {};
        const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();
        const label = fullName || user.username || user.email || "Sin nombre";
        return { id: employee.id, label };
      })
      .filter((item) => item.id)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employeesList]);

  const errorManagerLeaveRequestsText = getErrorText(errorManagerLeaveRequests);
  const errorCreateText = getErrorText(errorCreateManagerLeaveRequest);
  const errorEmployeesText = getErrorText(errorEmployeesList);
  const errorUpdateText = getErrorText(errorUpdateManagerLeaveRequest);
  const errorDeleteText = getErrorText(errorDeleteManagerLeaveRequest);

  const deleteEmployeeName = requestToDelete ? getEmployeeName(requestToDelete) : "—";
  const deleteType = requestToDelete ? renderLeaveTypeLabel(requestToDelete.leave_type) : "—";
  const deleteRange = requestToDelete
    ? `${requestToDelete.start_date || "—"} → ${requestToDelete.end_date || "—"}`
    : "—";

  return (
    <div className="hr-manager-tab hr-manager-vacations-tab">
      {/* Header card */}
      <div className="hr-manager-vacations-header-card">
        <div className="hr-manager-vacations-header-left">
          <p className="hr-manager-vacations-kicker">Ausencias</p>
          <h2 className="hr-manager-vacations-title">Vacaciones y ausencias del equipo</h2>
          <p className="hr-manager-vacations-subtitle">
            Gestiona solicitudes (aprobar / rechazar) y registra ausencias manualmente cuando haga falta.
          </p>
        </div>

        <div className="hr-manager-vacations-header-right">
          <div className="hr-manager-vacations-pill">
            <CalendarDays className="hr-manager-vacations-pill-icon" aria-hidden="true" />
            <span className="hr-manager-vacations-pill-text">
              Estado: <strong>{renderStatusLabel(statusFilter)}</strong>
            </span>
          </div>

          <div className="hr-manager-vacations-pill hr-manager-vacations-pill--secondary">
            <Users className="hr-manager-vacations-pill-icon" aria-hidden="true" />
            <span className="hr-manager-vacations-pill-text">{metricTotal} solicitudes</span>
          </div>
        </div>
      </div>

      {/* Card creación */}
      <div className="hr-manager-vacations-create-card">
        <div className="hr-manager-vacations-create-header">
          <h3 className="hr-manager-vacations-create-title">Crear ausencia para un empleado</h3>
          <p className="hr-manager-vacations-create-helper">
            Úsalo para registrar vacaciones, un día libre o una baja directamente.
          </p>
        </div>

        <form noValidate className="hr-manager-vacations-create-form" onSubmit={handleCreateForEmployee}>
          <div className="hr-manager-vacations-create-grid">
            <div className="hr-manager-vacations-field">
              <label>Empleado</label>
              <select
                value={selectedEmployeeId}
                onChange={(event) => setSelectedEmployeeId(event.target.value)}
                disabled={loadingEmployeesList}
                required
              >
                <option value="">
                  {loadingEmployeesList ? "Cargando empleados..." : "Seleccionar empleado"}
                </option>
                {employeeOptions.map((option) => (
                  <option key={option.id} value={String(option.id)}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="hr-manager-vacations-field">
              <label>Tipo de ausencia</label>
              <select value={leaveType} onChange={(event) => setLeaveType(event.target.value)}>
                <option value="vacation">Vacaciones</option>
                <option value="day_off">Día libre</option>
                <option value="sick_leave">Baja médica</option>
                <option value="other">Otros</option>
              </select>
            </div>

            <div className="hr-manager-vacations-field">
              <label>Fecha inicio</label>
              <input
                type="date"
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  if (endDate && event.target.value && endDate < event.target.value) {
                    setEndDate(event.target.value);
                  }
                }}
                required
              />
            </div>

            <div className="hr-manager-vacations-field">
              <label>Fecha fin</label>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(event) => setEndDate(event.target.value)}
                required
              />
            </div>
          </div>

          <div className="hr-manager-vacations-field hr-manager-vacations-field--full">
            <label>Motivo (opcional)</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ej. Vacaciones de verano, asunto personal, baja médica..."
            />
          </div>

          <div className="hr-manager-vacations-create-footer">
            <button
              type="submit"
              className="hr-button hr-button--primary"
              disabled={
                loadingCreateManagerLeaveRequest ||
                loadingEmployeesList ||
                !selectedEmployeeId ||
                !startDate ||
                !endDate
              }
            >
              {loadingCreateManagerLeaveRequest ? "Creando..." : "Crear ausencia"}
            </button>

            {createSuccessMessage && <span className="hr-success-text">{createSuccessMessage}</span>}
            {errorCreateText && <span className="hr-error-inline">{errorCreateText}</span>}
            {errorEmployeesText && <span className="hr-error-inline">Error empleados: {errorEmployeesText}</span>}
          </div>
        </form>
      </div>

      {/* Filtros listado */}
      <div className="hr-manager-vacations-filters-card">
        <div className="hr-manager-vacations-filter-row">
          <div className="hr-filter-select">
            <label>Estado</label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="pending">Pendientes</option>
              <option value="approved">Aprobadas</option>
              <option value="rejected">Rechazadas</option>
              <option value="cancelled">Canceladas</option>
              <option value="all">Todos los estados</option>
            </select>
          </div>

          <button
            type="button"
            className="hr-button hr-button--primary"
            onClick={handleSearch}
            disabled={loadingManagerLeaveRequests}
          >
            {loadingManagerLeaveRequests ? "Cargando..." : "Aplicar filtros"}
          </button>
        </div>

        {(errorManagerLeaveRequestsText || errorUpdateText || errorDeleteText) && (
          <div className="hr-error-banner">
            {errorManagerLeaveRequestsText && (
              <span>Error al cargar solicitudes: {errorManagerLeaveRequestsText}</span>
            )}
            {errorUpdateText && <span>Error al editar solicitud: {errorUpdateText}</span>}
            {errorDeleteText && <span>Error al eliminar solicitud: {errorDeleteText}</span>}
          </div>
        )}
      </div>

      {/* Métricas */}
      <div className="hr-manager-vacations-metrics">
        <div className="hr-metric-card">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Solicitudes</span>
            <Users className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">
            {metricTotal}
            <span className="hr-metric-unit">total</span>
          </div>
          <p className="hr-metric-helper">Total de solicitudes para el filtro actual.</p>
        </div>

        <div className="hr-metric-card hr-metric-card--primary">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Estado seleccionado</span>
            <CalendarDays className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">{renderStatusLabel(statusFilter)}</div>
          <p className="hr-metric-helper">Vista por estado (o todos).</p>
        </div>
      </div>

      {/* Tabla */}
      <div className="hr-manager-vacations-table-card">
        <div className="hr-manager-vacations-table-header">
          <h3 className="hr-manager-vacations-table-title">Solicitudes de ausencias</h3>
          <div className="hr-manager-vacations-table-pill">
            <Users className="hr-manager-vacations-table-pill-icon" aria-hidden="true" />
            <span>{metricTotal} items</span>
          </div>
        </div>

        {loadingManagerLeaveRequests ? (
          <div className="hr-manager-vacations-empty">Cargando solicitudes...</div>
        ) : !hasRequests ? (
          <div className="hr-manager-vacations-empty">No hay solicitudes para el filtro seleccionado.</div>
        ) : (
          <div className="hr-time-history-table-wrapper">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Tipo</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Días</th>
                  <th>Estado</th>
                  <th style={{ textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {managerLeaveRequests.map((request) => {
                  const status = request.status;
                  const canManage = status === "pending";
                  const isProcessing = processingId === request.id;

                  const disableRowActions =
                    isProcessing ||
                    loadingUpdateManagerLeaveRequest ||
                    loadingDeleteManagerLeaveRequest;

                  return (
                    <tr key={request.id}>
                      <td>{getEmployeeName(request)}</td>
                      <td>{renderLeaveTypeLabel(request.leave_type)}</td>
                      <td>{request.start_date}</td>
                      <td>{request.end_date}</td>
                      <td>{request.total_days ?? "—"}</td>
                      <td>
                        <span className={getStatusBadgeClass(status)}>
                          {renderStatusLabel(status)}
                        </span>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <div className="hr-manager-vacations-actions">
                          {canManage ? (
                            <div className="hr-manager-vacations-manage-actions">
                              <button
                                type="button"
                                className="hr-button hr-button--small hr-button--success"
                                onClick={() => handleApprove(request)}
                                disabled={isProcessing}
                              >
                                <Check
                                  className="hr-button-icon"
                                  aria-hidden="true"
                                />
                                {isProcessing ? "Procesando..." : "Aprobar"}
                              </button>

                              <button
                                type="button"
                                className="hr-button hr-button--small hr-button--danger"
                                onClick={() => handleReject(request)}
                                disabled={isProcessing}
                              >
                                <X
                                  className="hr-button-icon"
                                  aria-hidden="true"
                                />
                                {isProcessing ? "Procesando..." : "Rechazar"}
                              </button>
                            </div>
                          ) : null}

                          <div className="hr-manager-vacations-icon-actions">
                            <button
                              type="button"
                              className="hr-icon-button"
                              onClick={() => openEditModal(request)}
                              disabled={disableRowActions}
                              aria-label="Editar solicitud"
                              title="Editar solicitud"
                            >
                              <Pencil
                                width={16}
                                height={16}
                                aria-hidden="true"
                              />
                            </button>

                            <button
                              type="button"
                              className="hr-icon-button hr-icon-button--danger"
                              onClick={() => openDeleteModal(request)}
                              disabled={disableRowActions}
                              aria-label="Eliminar solicitud"
                              title="Eliminar solicitud"
                            >
                              <Trash2
                                width={16}
                                height={16}
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL EDITAR */}
      {editModalOpen && (
        <div className="ui-modal-overlay" onClick={closeEditModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ui-edit-leave-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3 id="ui-edit-leave-title">Editar ausencia</h3>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                <strong>{requestToEdit ? getEmployeeName(requestToEdit) : "Empleado"}</strong>
                <div className="ui-delete-subhelp">
                  Modifica tipo y fechas. Guarda para aplicar cambios.
                </div>
              </div>

              <div className="ui-field">
                <div className="ui-label">Tipo</div>
                <select
                  className="ui-input"
                  value={editLeaveType}
                  onChange={(e) => setEditLeaveType(e.target.value)}
                  disabled={loadingUpdateManagerLeaveRequest}
                >
                  <option value="vacation">Vacaciones</option>
                  <option value="day_off">Día libre</option>
                  <option value="sick_leave">Baja médica</option>
                  <option value="other">Otros</option>
                </select>
              </div>

              <div className="ui-field">
                <div className="ui-label">Fecha inicio</div>
                <input
                  className="ui-input"
                  type="date"
                  value={editStartDate}
                  onChange={(e) => {
                    setEditStartDate(e.target.value);
                    if (editEndDate && e.target.value && editEndDate < e.target.value) {
                      setEditEndDate(e.target.value);
                    }
                  }}
                  disabled={loadingUpdateManagerLeaveRequest}
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Fecha fin</div>
                <input
                  className="ui-input"
                  type="date"
                  value={editEndDate}
                  min={editStartDate || undefined}
                  onChange={(e) => setEditEndDate(e.target.value)}
                  disabled={loadingUpdateManagerLeaveRequest}
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Motivo (opcional)</div>
                <textarea
                  className="ui-input"
                  rows={3}
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  disabled={loadingUpdateManagerLeaveRequest}
                />
              </div>

              {errorUpdateText ? (
                <div className="ui-modal-error" role="alert" aria-live="polite">
                  {errorUpdateText}
                </div>
              ) : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeEditModal}
                disabled={loadingUpdateManagerLeaveRequest}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={confirmEdit}
                disabled={loadingUpdateManagerLeaveRequest || !editStartDate || !editEndDate}
              >
                {loadingUpdateManagerLeaveRequest ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR (sin X) */}
      {deleteModalOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ui-delete-leave-title"
            aria-describedby="ui-delete-leave-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3 id="ui-delete-leave-title">Eliminar ausencia</h3>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help" id="ui-delete-leave-desc">
                ¿Seguro que quieres eliminar esta ausencia?
                <div className="ui-delete-subhelp">
                  <strong>{deleteEmployeeName}</strong> · {deleteType} · {deleteRange}
                </div>
                <div className="ui-delete-subhelp">Esta acción no se puede deshacer.</div>
              </div>

              {errorDeleteText ? (
                <div className="ui-modal-error" role="alert" aria-live="polite">
                  {errorDeleteText}
                </div>
              ) : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteModal}
                disabled={loadingDeleteManagerLeaveRequest}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDelete}
                disabled={loadingDeleteManagerLeaveRequest}
              >
                {loadingDeleteManagerLeaveRequest ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrManagerVacationsTab;